import React, {useState, useEffect} from 'react';
import {Link, useLocation, useHistory} from 'react-router-dom';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {useDebouncedCallback} from 'use-debounce';
import Box from '@material-ui/core/Box';
import EditIcon from '@material-ui/icons/Edit';
import {setQuery} from '@pluto/set-history-query';
import Table, {
    Sort,
    getInitialSort,
    getInitialPage,
    getInitialRowsPerPage,
} from '@pluto/table';
import useQuery from '@pluto/query-hook';
import {getNewSort} from '@pluto/qc-sort';
import {OnInputChangeEvent} from '@pluto/@types';
import {getOptsFromQuery} from '@pluto/select';
import {adminRoutes} from 'App/AppRouter/routes';
import ContentPartnerToolbar from './ContentPartnersToolbar';
import ContentPartnersColumns from './ContentPartnersColumns';
import ContentPartnersFilters from './ContentPartnersFilters';
import {
    ContentPartnersFiltersHandlers,
    ContentPartnersFiltersType,
    ContentPartnerFiltersKeys,
    HandleMountParams,
    ContentPartnersResponse,
    SetContentPartnersAction,
} from './ContentPartnersTypes';
import './ContentPartners.scss';

interface ContentPartnersProps {
    contentPartners: ContentPartnersResponse[];
    total: number;
    setContentPartners: SetContentPartnersAction;
}

export const renderEditIcon = curry((location, {id}) => {
    const url = `/content-partners-edit/${id}/${location}`;

    return (
        <Link className="edit-icon-link" to={url}>
            <EditIcon />
        </Link>
    );
});

export const handlePaging = curry((containerArgs, tablePaging) => {
    const {
        setContentPartners,
        setQuery,
        history,
        route,
        ...containerParams
    } = containerArgs;
    const params = {
        ...containerParams,
        ...tablePaging,
    };

    setQuery(history, route, params);
    setContentPartners(params);
});

export const handleDebounce = curry(({debouncedFunction, setName}, {target}) => {
    const {value} = target;

    setName(value);
    debouncedFunction(value);
});

export const handleDebounceCallback = curry((handlers, name) => {
    const {setContentPartners, setPage, setQuery, history, route, ...params} = handlers;
    const newQuery = {
        ...params,
        name,
    };

    setPage(0);
    setQuery(history, route, newQuery);
    setContentPartners(newQuery);
});

export const handleMount = wrap((props: HandleMountParams) => {
    const {setContentPartners, setPage, setRowsPerPage, params, query} = props;
    const queryLimit = query.get('limit');

    if (queryLimit) {
        const queryOffset = query.get('offset');
        const limit = Number(queryLimit);
        const page = Number(queryOffset) / limit || 0;

        setRowsPerPage(limit);
        setPage(page);
        setContentPartners({
            ...params,
            limit: Number(queryLimit),
        });
        return;
    }

    setContentPartners({
        ...params,
        limit: params.limit,
    });
});

export const handleSort = curry((componentParams, oldSort) => {
    const {setSort, getList, setQuery, history, params, route} = componentParams;

    const newSort = getNewSort(oldSort);
    setSort(newSort);

    const newQuery = {
        ...params,
        sortBy: newSort.sortBy,
        sortOrder: newSort.sortOrder,
    };

    setQuery(history, route, newQuery);
    getList(newQuery);
});

export const setFilterValue = (
    {target}: OnInputChangeEvent,
    state: string[] = []
): string[] => {
    const {name} = target;

    if (state.includes(name)) {
        return [];
    }

    return [name];
};

export const handleFilters = curry(
    (filterHandlers: ContentPartnersFiltersHandlers, event: OnInputChangeEvent) => {
        const {
            setFilters,
            setPage,
            setQuery,
            history,
            getList,
            limit,
            roles,
            name,
            sortOrder,
            sortBy,
            route,
            key,
        } = filterHandlers;

        const query = {
            name,
            roles,
            [key]: setFilterValue(event, filterHandlers[key]),
            offset: 0,
            limit,
            sortOrder,
            sortBy,
        };
        setPage(0);
        setQuery(history, route, query);
        setFilters(query);
        getList(query);
    }
);

const DEBOUNCE_TIME = 700;

export default function ContentPartnersContainer(props: ContentPartnersProps) {
    const {contentPartners, total, setContentPartners} = props;
    const query = useQuery();
    const history = useHistory();
    const {search} = useLocation();
    const getFromQuery = getOptsFromQuery(query);
    const [name, setName] = useState('');
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const [page, setPage] = useState(initialPage);
    const initialSort = getInitialSort(query);
    const [sort, setSort] = useState<Sort>(initialSort);
    const [filters, setFilters] = useState<ContentPartnersFiltersType>({
        active: getFromQuery('active'),
    });

    const params = {
        ...sort,
        ...filters,
        name,
        offset: rowsPerPage * page,
        limit: rowsPerPage,
    };

    const debounceCallback = handleDebounceCallback({
        ...params,
        offset: 0,
        setContentPartners,
        setPage,
        setQuery,
        history,
    });

    const filterHandlers = {
        ...params,
        history,
        route: adminRoutes.contentPartners,
        setFilters,
        setPage,
        setQuery,
        getList: setContentPartners,
    };
    const [debouncedFunction] = useDebouncedCallback(debounceCallback, DEBOUNCE_TIME);

    const onDebouncedSearch = handleDebounce({
        debouncedFunction,
        setName,
    });

    const onPaging = handlePaging({
        ...params,
        setContentPartners,
        setQuery,
        history,
    });

    const onMount = handleMount({
        setContentPartners,
        setRowsPerPage,
        setPage,
        params,
        query,
    });

    useEffect(onMount, []);

    const cols = [
        ...ContentPartnersColumns,
        {
            dataKey: '',
            title: '',
            render: renderEditIcon(search),
        },
    ];

    const onSort = handleSort({
        params,
        history,
        setSort,
        setQuery,
        getList: setContentPartners,
        route: adminRoutes.contentPartners,
    });

    const onStatusFilter = handleFilters({
        ...filterHandlers,
        key: ContentPartnerFiltersKeys.Status,
    });

    return (
        <Box className="content-partners-container">
            <ContentPartnerToolbar handleNameChange={onDebouncedSearch} name={name} />
            <ContentPartnersFilters onStatusFilter={onStatusFilter} filters={filters} />
            <Table
                data={contentPartners}
                columns={cols}
                total={total}
                onChangePageHandler={onPaging}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                setSort={onSort}
                sort={sort}
            />
        </Box>
    );
}
